import "./App.css";
import { useAuth0 } from "@auth0/auth0-react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { lazy, useEffect } from "react";
import { Layout } from "./Layout/Layout.tsx";
import { LoadingPage } from "./common/components/LoadingPage.tsx";
import { AuthenticationGuard } from "./common/auth/authenticationGuard.tsx";
import { ErrorBoundary } from "./common/components/ErrorBoundary.tsx";
import { DefaultErrorView } from "./common/components/DefaultErrorView.tsx";

const Dashboard = lazy(() => import("./Dashboard/Dashboard.tsx"));
const Project = lazy(() => import("./Project/Project.tsx"));
const Requests = lazy(() => import("./Requests/Requests.tsx"));
const PrivacyPolicy = lazy(() => import("./PrivacyPolicy/PrivacyPolicy.tsx"));
const TermsOfUse = lazy(() => import("./TermsOfUse/TermsOfUse.tsx"));
const AboutUs = lazy(() => import("./AboutUs/AboutUs.tsx"));
const Registration = lazy(() => import("./Registration/Registration.tsx"));
const Users = lazy(() => import("./Users/Users.tsx"));
const PayReport = lazy(() => import("./Reports/PayReport/PayReport.tsx"));
const RoofCalculator = lazy(() => import("./Tools/RoofCalculator.tsx"));
const DealInitiation = lazy(
  () => import("./Aurora/SoldForm/DealSubmissionForm.tsx"),
);
const Profile = lazy(() => import("./Profile/Profile.tsx"));
import { Outlet } from "react-router-dom";
import PasswordlessLogin from "./common/auth/PasswordlessLogin.tsx";

const Wrap = () => {
  return (
    <Layout>
      <ErrorBoundary fallback={<DefaultErrorView />}>
        <Outlet />
      </ErrorBoundary>
    </Layout>
  );
};

function App() {
  const navigateTo = useNavigate();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    window.navigateTo = navigateTo;
  }, [navigateTo]);

  const { isLoading, isAuthenticated, error, getAccessTokenSilently, logout } =
    useAuth0();
  useEffect(() => {
    getAccessTokenSilently().catch((err) => {
      if (
        err.error === "missing_refresh_token" &&
        isLoading &&
        isAuthenticated
      ) {
        logout().then((r) => console.log(r));
      }
    });
  }, [getAccessTokenSilently, isLoading, isAuthenticated, logout]);

  if (error) {
    return <div>Oops... {error.message}</div>;
  }

  if (isLoading) {
    return <LoadingPage isFullPage={true} />;
  }

  return (
    <>
      <Routes>
        <Route element={<Wrap />}>
          <Route
            path="/"
            element={<AuthenticationGuard component={Dashboard} />}
          />
          <Route path="/project/:id" element={<Project />} />
          <Route path="/project/:id/*" element={<Project />} />
          <Route
            path={"/request"}
            element={<AuthenticationGuard component={Requests} />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route
            path="/profile"
            element={<AuthenticationGuard component={Profile} />}
          />
          <Route path="/pay-report" element={<PayReport />} />
          <Route
            path="/tools/roof-calculator"
            element={<AuthenticationGuard component={RoofCalculator} />}
          />
          <Route
            path="/deal-submission"
            element={<AuthenticationGuard component={DealInitiation} />}
          />
          <Route
            path={"/register/*"}
            element={<AuthenticationGuard component={Registration} />}
          />
          <Route
            path={"/users/*"}
            element={<AuthenticationGuard component={Users} />}
          />
        </Route>
        <Route path="/register" element={<Registration />} />
        <Route
          path="/login"
          element={
            <PasswordlessLogin
              domain={import.meta.env.VITE_PUBLIC_AUTH0_DOMAIN}
              clientId={import.meta.env.VITE_PUBLIC_AUTH0_CLIENT_ID}
              onLogin={() => {
                const params = new URLSearchParams(window.location.search);
                const returnTo = params.get("returnTo") || "/";
                window.location.assign(returnTo);
              }}
            />
          }
        />
      </Routes>
    </>
  );
}

export default App;
